'use client'

// The monorail's lift, as the viewer has it: where each car is now and how
// open its doors are (from the town's entity, monorail_lift_<i>, on the
// town's clock), the doors as walls for a visitor while they are shut, and
// what a visitor standing by it can ask for. The walker, the bird's drawing
// and the models all read it here, so a visitor in the car, Pino in the car
// and the car itself rise together.
//
// The town runs the car (sim-server/src/world/Lift.js); a visitor's call is
// a message to it, and everyone sees the same car, so a visitor can ride up
// with him.

import useGameStore from '../../stores/gameStore'
import { liftLayout, liftNow, LIFT } from './monorail'
import { liftBeep } from '../audio/lift'

let laid = null
/** The lifts' layout, laid out once. */
export function allLifts() { return laid || (laid = liftLayout()) }

/** The town's time, as near as the viewer can tell it. */
export function townNow() { return Date.now() + (useGameStore.getState().serverClockOffset || 0) }

const stateOf = (i) => useGameStore.getState().entityStates.find((e) => e.id === `monorail_lift_${i}`)?.state || null

/** Each lift now: { lf, state, y, moving, doors: [bottom, top] }. */
export function liftsNow(now = townNow()) {
  return allLifts().map((lf) => {
    const state = stateOf(lf.i)
    return { lf, state, ...liftNow(lf, state, now) }
  })
}

/** Inside a car's floor, in the town's (x, z): the lift, or null. */
export function inCar(x, z, pad = 0) {
  for (const lf of allLifts()) {
    const { u, d } = lf.frameOf(x, z)
    const c = lf.frameOf(lf.car.x, lf.car.z)
    if (Math.abs(u - c.u) <= lf.car.hu - pad && Math.abs(d - c.d) <= lf.car.hd - pad) return lf
  }
  return null
}

// a landing's door gap as a box in the walker's shape, made once
const doorBoxes = new Map()
function doorBox(lf, level) {
  const key = `${lf.i}_${level}`
  if (doorBoxes.has(key)) return doorBoxes.get(key)
  const L = LIFT
  const cu = (L.u0 + L.u1) / 2, cd = (L.d0 + L.d1) / 2
  const [u0, u1, d0, d1, y0, y1] = level
    ? [L.u0, L.u0 + L.wall, cd - L.highDoor / 2, cd + L.highDoor / 2, lf.high, lf.high + L.doorHeight]
    : [cu - L.lowDoor / 2, cu + L.lowDoor / 2, L.d1 - L.wall, L.d1, lf.low, lf.low + L.doorHeight]
  const [cx, cz] = lf.at((u0 + u1) / 2, (d0 + d1) / 2)
  const len = Math.hypot(lf.U[0], lf.U[1]) || 1
  const b = { cx, cz, hx: (u1 - u0) / 2, hz: (d1 - d0) / 2, ca: lf.U[0] / len, sa: -lf.U[1] / len, topY: y1, bottomY: y0, solid: true }
  doorBoxes.set(key, b)
  return b
}

/** The doors that are shut (or not open enough to pass), as walls for the walker. */
export function shutDoors(now = townNow(), out = []) {
  out.length = 0
  for (const { lf, doors } of liftsNow(now)) {
    if (doors[0] < 0.85) out.push(doorBox(lf, 0))
    if (doors[1] < 0.85) out.push(doorBox(lf, 1))
  }
  return out
}

const NEAR = 2.6

/**
 * What a visitor at (x, feet y, z) can ask of a lift: call it to the
 * landing they are on, or, in the car, send it to the other one. Null
 * when they are nowhere near one, or it is on its way.
 */
export function liftAsk(x, y, z, now = townNow()) {
  for (const { lf, state, moving } of liftsNow(now)) {
    if (inCar(x, z, 0.1) === lf) {
      if (moving) return null
      const to = state?.level ? 0 : 1
      return { lift: lf.i, level: to, words: to ? 'go up to the station' : 'go down to the street' }
    }
    if (Math.hypot(x - lf.standLow.x, z - lf.standLow.z) < NEAR && y < lf.low + 1.5) return { lift: lf.i, level: 0, words: 'call the lift' }
    if (Math.hypot(x - lf.standHigh.x, z - lf.standHigh.z) < NEAR && y > lf.high - 1) return { lift: lf.i, level: 1, words: 'call the lift' }
  }
  return null
}

let lastCall = 0
/** Ask the town for the car, at most once a second and a half from here (the town keeps its own count too). */
export function callLift(ask) {
  if (!ask) return false
  const now = Date.now()
  if (now - lastCall < 1500) return false
  const send = useGameStore.getState().signalSender
  if (!send) return false
  lastCall = now
  liftBeep()
  return send({ type: 'lift_call', lift: ask.lift, level: ask.level }) !== false
}
